import { createAction, unwrapResult } from '@reduxjs/toolkit';
import { InventoryConfiguration, InventoryItem, SkuItem } from 'common/modules/inventory/types';
import { ListenerManager } from 'common/services/ListenerManager';
import { createAppThunk } from 'services/utils/redux';
import { api } from 'common/frontend/api';

const inventoryListeners = new ListenerManager<'items' | 'skus' | 'configurations'>();

export const setInventoryItemsData = createAction<InventoryItem[]>(
	'Inventory/SET_INVENTORY_ITEMS_DATA',
);
export const setInventoryItemsError = createAction<string>('Inventory/SET_INVENTORY_ITEMS_ERROR');
export const setInventoryItemsListener = createAppThunk(
	'Inventory/SET_INVENTORY_ITEMS_LISTENER',
	(shopId: string, thunkAPI) => {
		return new Promise<InventoryItem[]>((resolve, reject) => {
			inventoryListeners.update({
				id: 'items',
				key: shopId,
				listener: api()
					.inventoryItems.get.where('shopId', '==', shopId)
					.onSnapshot(
						(items) => {
							thunkAPI.dispatch(setInventoryItemsData(items));
							return resolve(items);
						},
						(error) => {
							const err = error.message ?? 'Error getting stock items';
							thunkAPI.dispatch(setInventoryItemsError(err));
							return reject(err);
						},
					),
			});
		});
	},
);
export const clearInventoryItemsListener = createAppThunk(
	'Inventory/CLEAR_INVENTORY_ITEMS_LISTENER',
	() => inventoryListeners.clear('items'),
);

export const setSkuItemsData = createAction<SkuItem[]>('Inventory/SET_SKU_ITEMS_DATA');
export const setSkuItemsError = createAction<string>('Inventory/SET_SKU_ITEMS_ERROR');
export const setSkuItemsListener = createAppThunk(
	'Inventory/SET_SKU_ITEMS_LISTENER',
	(shopId: string, thunkAPI) => {
		return new Promise<SkuItem[]>((resolve, reject) => {
			inventoryListeners.update({
				id: 'skus',
				key: shopId,
				listener: api()
					.skuItems.get.where('shopId', '==', shopId)
					.onSnapshot(
						(units) => {
							thunkAPI.dispatch(setSkuItemsData(units));
							return resolve(units);
						},
						(error) => {
							const err = error.message ?? 'Error getting stock units';
							thunkAPI.dispatch(setSkuItemsError(err));
							return reject(err);
						},
					),
			});
		});
	},
);
export const clearSkuItemsListener = createAppThunk('Inventory/CLEAR_SKU_ITEMS_LISTENER', () =>
	inventoryListeners.clear('skus'),
);

export const setInventoryConfigurationData = createAction<InventoryConfiguration | undefined>(
	'Inventory/SET_INVENTORY_CONFIGURATION_DATA',
);
export const setInventoryConfigurationError = createAction<string>(
	'Inventory/SET_INVENTORY_CONFIGURATION_ERROR',
);
export const setInventoryConfigurationListener = createAppThunk(
	'Inventory/SET_INVENTORY_CONFIGURATION_LISTENER',
	(shopId: string, thunkAPI) => {
		return new Promise<InventoryConfiguration | undefined>((resolve, reject) => {
			inventoryListeners.update({
				id: 'configurations',
				key: shopId,
				listener: api()
					.inventoryConfigurations.doc(shopId)
					.listen(
						(configuration) => {
							thunkAPI.dispatch(setInventoryConfigurationData(configuration));
							return resolve(configuration);
						},
						(error) => {
							const err = error.message ?? 'Error getting stock units';
							thunkAPI.dispatch(setInventoryConfigurationError(err));
							return reject(err);
						},
					),
			});
		});
	},
);
export const clearInventoryConfigurationListener = createAppThunk(
	'Inventory/CLEAR_INVENTORY_CONFIGURATION_LISTENER',
	() => inventoryListeners.clear('configurations'),
);

export const setInventoryListeners = createAppThunk<boolean, string>(
	'Inventory/SET_INVENTORY_LISTENERS',
	async (shopId: string, thunkAPI) => {
		return new Promise<boolean>(async (resolve, reject) => {
			try {
				await Promise.all([
					thunkAPI.dispatch(setSkuItemsListener(shopId)).then(unwrapResult),
					thunkAPI.dispatch(setInventoryItemsListener(shopId)).then(unwrapResult),
					thunkAPI.dispatch(setInventoryConfigurationListener(shopId)).then(unwrapResult),
				]);
				return resolve(true);
			} catch (err) {
				return reject(err.message);
			}
		});
	},
);
export const clearActiveShopListeners = createAppThunk(
	'Inventory/CLEAR_INVENTORY_LISTENERS',
	() => {
		inventoryListeners.clearAll();
	},
);
